/**
 * Construction du payload createOrder pour /b/[slug] (événement).
 */
import type { CreateOrderPayload, TicketType } from "~/types/event";

interface AttendeeLocal {
  firstName: string;
  lastName: string;
  email: string;
  custom_data: Record<string, unknown>;
  ticketTypeId: string;
  ticketTypeName: string;
  selectedOptions: Record<string, number>;
}

export function useEventOrderPayload(opts: {
  ticketTypes: Ref<TicketType[]>;
  selections: Ref<Record<string, number>>;
  flexiblePrices: Ref<Record<string, number>>;
  attendees: Ref<AttendeeLocal[]>;
  buyerInfo: Ref<{ firstName: string; lastName: string; email: string; phone: string }>;
  includeFees: Ref<boolean>;
  donateUs: Ref<boolean>;
  donateToUsAmount: Ref<number>;
  donationAmount: Ref<number>;
  selectedProvider: Ref<"stripe" | "paypal">;
  promoApplied: Ref<{ code: string } | null>;
  /** Paiement en plusieurs fois : nombre de parts (0 = en une fois). */
  nombreParts?: Ref<number>;
}) {
  function buildOrderPayload(): CreateOrderPayload {
    const ticketSelections = Object.entries(opts.selections.value)
      .filter(([, qty]) => (qty as number) > 0)
      .map(([typeId, qty]) => {
        const tt = opts.ticketTypes.value.find((t) => t.id === typeId);
        const isFlexible = tt?.pricing === "flexible";
        return {
          ticket_type_id: typeId,
          quantity: qty as number,
          custom_price: isFlexible ? Number(opts.flexiblePrices.value[typeId] || 0) : undefined,
          attendees: opts.attendees.value
            .filter((a) => a.ticketTypeId === typeId)
            .map((a) => ({
              name: `${a.firstName} ${a.lastName}`,
              email: a.email,
              custom_data: a.custom_data,
              selected_options: Object.entries(a.selectedOptions)
                .filter(([, q]) => q > 0)
                .map(([option_id, quantity]) => ({ option_id, quantity })),
            })),
        };
      });

    const payload: CreateOrderPayload = {
      tickets: ticketSelections,
      buyer: {
        name: `${opts.buyerInfo.value.firstName} ${opts.buyerInfo.value.lastName}`,
        email: opts.buyerInfo.value.email,
        phone: opts.buyerInfo.value.phone || undefined,
      },
      include_fees: opts.includeFees.value,
      donate_to_us: opts.donateUs.value && opts.donateToUsAmount.value > 0
        ? Math.round(opts.donateToUsAmount.value * 100)
        : undefined,
      donation_amount: opts.donationAmount.value > 0
        ? Math.round(opts.donationAmount.value * 100)
        : undefined,
      payment_provider: opts.selectedProvider.value,
      ...((Number(opts.nombreParts?.value) || 0) >= 2
        ? { installments: Number(opts.nombreParts!.value) }
        : {}),
    };
    if (opts.promoApplied.value) {
      payload.promo_code = opts.promoApplied.value.code;
    }
    return payload;
  }

  return { buildOrderPayload };
}
